import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaCalendarAlt, FaRupeeSign, FaClock, FaSuitcase } from 'react-icons/fa';
import './DestinationDetailPage.css';

// This data would ideally come from a backend API
// For now, we'll use this static data that you can replace with your sample writings
const destinationsData = {
  'alleppey': {
    name: 'Alleppey Backwaters',
    tagline: 'Experience the serene backwaters on a traditional houseboat',
    heroImage: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944',
    overview: `Alleppey, also known as Alappuzha, is famous for its network of serene backwaters and houseboats. 
               Often referred to as the "Venice of the East," this coastal town offers a mesmerizing experience of 
               Kerala's backwaters, where you can navigate through the intricate network of canals, lagoons, and lakes.`,
    highlights: [
      'Overnight stay on traditional houseboats (Kettuvallams)',
      'Cruising through the tranquil backwater canals',
      'Experience local village life along the shores',
      'Bird watching in the Vembanad Lake region',
      'Visit to Pathiramanal Island, a haven for migratory birds'
    ],
    description: `
      <h3>A Backwater Paradise</h3>
      <p>Alleppey is the hub of Kerala's backwaters and a gateway to the intricate network of waterways that spread across the region. The backwaters are a chain of brackish lagoons and lakes lying parallel to the Arabian Sea coast. These waterways are home to unique aquatic life and offer a distinctive ecosystem to explore.</p>
      
      <h3>Houseboat Experience</h3>
      <p>The traditional houseboats, known as 'Kettuvallams', are converted rice barges with bedrooms, a living area, kitchen, and dedicated staff. These luxurious floating homes allow you to experience the backwaters in comfort while enjoying delicious local cuisine prepared fresh onboard. As you cruise along, you'll witness the daily lives of people who live along these waterways, engaged in fishing, farming, and coir making.</p>
      
      <h3>Alleppey Beach</h3>
      <p>Besides the backwaters, Alleppey is home to a beautiful beach with a 150-year-old pier extending into the sea. The beach hosts several events including beach festivals and offers spectacular sunsets. It's a perfect spot for relaxation after your backwater cruise.</p>
      
      <h3>Local Culture and Cuisine</h3>
      <p>The region has a rich cultural heritage reflected in its temples, churches, and local festivals. The cuisine of Alleppey is dominated by seafood and coconut-based dishes. Don't miss trying the 'Karimeen Pollichathu' (Pearl spot fish wrapped in banana leaf and grilled), a local delicacy often served on houseboats.</p>
      
      <h3>Coir Industry</h3>
      <p>Alleppey is also known for its coir industry. You can visit the coir factories to see how coconut fibers are transformed into beautiful mats, rugs, and other handicrafts.</p>
    `,
    bestTimeToVisit: 'November to February offers pleasant weather. August to September for the famous Snake Boat Race.',
    howToReach: {
      air: 'Nearest airport is Cochin International Airport (85 km)',
      train: 'Alleppey has its own railway station well connected to major cities',
      road: 'Well-connected by road from Kochi (53 km) and Trivandrum (159 km)'
    },
    activities: [
      {
        name: 'Houseboat Cruise',
        description: 'Explore the backwaters on traditional houseboats, either for day trips or overnight stays'
      },
      {
        name: 'Village Tours',
        description: 'Experience the local village life along the backwaters'
      },
      {
        name: 'Beaches',
        description: 'Relax at Alleppey Beach and Marari Beach known for its serene environment'
      },
      {
        name: 'Snake Boat Races',
        description: 'Witness the famous Nehru Trophy Boat Race if visiting during August-September'
      },
      {
        name: 'Coir Factory Visits',
        description: 'Observe the traditional coir-making process at local factories'
      }
    ],
    accommodation: [
      {
        type: 'Houseboats',
        description: 'Various categories from budget to luxury houseboats'
      },
      {
        type: 'Resorts',
        description: 'Backwater and beach resorts offering authentic Kerala experiences'
      },
      {
        type: 'Homestays',
        description: 'Experience local hospitality with traditional Kerala families'
      }
    ],
    nearbyDestinations: [
      {
        name: 'Kumarakom',
        distance: '35 km',
        description: 'Another backwater destination with a renowned bird sanctuary'
      },
      {
        name: 'Marari Beach',
        distance: '16 km',
        description: 'A serene beach perfect for relaxation and swimming'
      },
      {
        name: 'Kochi',
        distance: '53 km',
        description: 'A historic port city with colonial influences and cultural diversity'
      }
    ],
    gallery: [
      '/images/gallery/destinations/alleppey1.jpg',
      '/images/gallery/destinations/alleppey2.jpg',
      '/images/gallery/destinations/alleppey3.jpg'
    ]
  },
  
  'munnar': {
    name: 'Munnar Tea Gardens',
    tagline: 'The Enchanting Hill Station nestled in the Western Ghats',
    heroImage: 'https://images.unsplash.com/photo-1470115636492-6d2b56f9146d',
    overview: `Munnar, nestled in the Western Ghats of Kerala, is one of India's most picturesque hill stations. 
              Located at an altitude of about 1,600 meters (5,200 feet) above sea level, Munnar is known for its 
              sprawling tea plantations, mist-covered mountains, lush valleys, and a cool, pleasant climate.`,
    highlights: [
      'Expansive tea plantations covering over 12,000 hectares',
      'Breathtaking views from Top Station, the highest point in Munnar',
      'Wildlife spotting at Eravikulam National Park, home to the endangered Nilgiri Tahr',
      'The rare Neelakurinji flowers that bloom once every 12 years',
      'Serene lakes and dams perfect for boating and picnicking'
    ],
    description: `
      <h3>Colonial Heritage</h3>
      <p>This hill station at a high altitude was once the summer resort of the British government in South India. The colonial bungalows in the hillocks boast of the glorious past of this place. The pleasant romantic climate and the laid-back atmosphere in Munnar made it the most preferred honeymoon location in the country.</p>
      
      <h3>The Three Rivers</h3>
      <p>The word Munnar literally means "three rivers." As the name refers, Munnar lies at the confluence of three rivers—Muthirapuzha, Nallathanni, and Kundala—in the Idukki district.</p>
      
      <h3>Tea Plantations</h3>
      <p>One of the major attractions of Munnar is the tea plantations. There are more than 12,000 hectares of lush manicured tea fields forming a picturesque background for the place. Visitors can tour tea factories to learn about the processing of tea and sample different varieties.</p>
      
      <h3>Natural Beauty</h3>
      <p>The beautiful valleys covered with coffee, tea, and spice plantations, the misty hillsides sheltering many endangered species of wildlife, and stunning waterfalls offer a refreshing experience. But perhaps most refreshing is the pure, unpolluted air that envelops you as soon as you arrive.</p>
      
      <h3>Neelakurinji Phenomenon</h3>
      <p>The Neelakurinji flower, one of the rarest and most beautiful flowers which blooms once in twelve years, is found in the valleys of Munnar. When these flowers bloom, they cover the entire region with their beautiful violet color, offering a rare spectacle that attracts visitors from around the world.</p>
      
      <h3>Top Attractions</h3>
      <p><strong>1. Tea Gardens & Tea Museum</strong> – Munnar is synonymous with tea, and a visit to the lush tea estates and the Tata Tea Museum offers insights into tea processing and history.</p>
      
      <p><strong>2. Eravikulam National Park</strong> – Home to the endangered Nilgiri Tahr, this national park is a must-visit for wildlife enthusiasts and offers breathtaking views of rolling hills.</p>
      
      <p><strong>3. Mattupetty Dam & Lake</strong> – A picturesque spot for boating and picnicking, surrounded by verdant hills.</p>
      
      <p><strong>4. Echo Point</strong> – Famous for its natural echo phenomenon, this spot is perfect for photography and leisurely walks.</p>
      
      <p><strong>5. Top Station</strong> – The highest point in Munnar, offering panoramic views of the Western Ghats and Tamil Nadu's plains.</p>
      
      <p><strong>6. Attukal Waterfalls</strong> – A mesmerizing cascade surrounded by greenery, ideal for trekking and nature lovers.</p>
      
      <p><strong>7. Kundala Lake</strong> – Known for its serene ambiance, pedal boating, and stunning cherry blossom trees.</p>
      
      <p><strong>8. Marayoor Sandalwood Forests</strong> – A unique attraction with aromatic sandalwood trees and prehistoric dolmens.</p>
      
      <h3>Local Cuisine & Shopping</h3>
      <p>Kerala is known as the Mecca of SPICES and TEA. The top-quality spices and fresh tea are available in the local market at a reasonable price. Munnar's local cuisine features delicious Kerala dishes like Appam with Stew, Kerala Parotta, Malabar Biryani, and Fish Curry. For shopping, visitors can buy handmade chocolates and aromatic essential oils as souvenirs.</p>
      
      <h3>Activities</h3>
      <p>There are a variety of interesting activities available in Munnar. You can observe wild animals, play golf, or try trout fishing. Trekking through the tea plantations and spice gardens offers an immersive experience of the region's natural beauty.</p>
      
      <h3>Final Thoughts</h3>
      <p>Munnar is more than just a hill station; it's an experience of nature's finest beauty. Whether you want to sip on freshly brewed tea, trek through scenic trails, or relax amidst misty landscapes, Munnar promises a rejuvenating escape from city life. So, don't forget to make it a 'once in a lifetime' experience.</p>
    `,
    bestTimeToVisit: 'September to March is ideal with cool, pleasant weather. The monsoon months (June-August) bring lush greenery.',
    howToReach: {
      air: 'Nearest airport is Cochin International Airport (110 km away)',
      train: 'Closest railway stations are Aluva (110 km) and Ernakulam (130 km)',
      road: 'Well-connected by road from cities like Kochi, Madurai, and Coimbatore with scenic drives through winding roads and misty hills'
    },
    activities: [
      {
        name: 'Tea Garden Tours',
        description: 'Visit the sprawling tea estates and the Tata Tea Museum to learn about tea processing and history'
      },
      {
        name: 'Wildlife Spotting',
        description: 'Explore Eravikulam National Park to see the endangered Nilgiri Tahr and other wildlife'
      },
      {
        name: 'Boating',
        description: 'Enjoy pedal boating at Mattupetty Dam, Kundala Lake, and other serene water bodies'
      },
      {
        name: 'Trekking',
        description: 'Embark on trails through scenic landscapes and to viewpoints like Top Station'
      },
      {
        name: 'Photography',
        description: 'Capture stunning vistas at Echo Point, known for its natural echo phenomenon'
      },
      {
        name: 'Spice Garden Tours',
        description: 'Visit spice plantations to learn about various spices grown in the region'
      }
    ],
    accommodation: [
      {
        type: 'Luxury Resorts',
        description: 'High-end properties offering panoramic views of tea gardens and mountains'
      },
      {
        type: 'Tea Estate Bungalows',
        description: 'Colonial-era accommodations within tea estates offering an authentic experience'
      },
      {
        type: 'Boutique Hotels',
        description: 'Charming properties with personalized service and local character'
      },
      {
        type: 'Homestays',
        description: 'Experience local hospitality and culture with traditional Kerala families'
      }
    ],
    nearbyDestinations: [
      {
        name: 'Thekkady',
        distance: '100 km',
        description: 'Home to Periyar Wildlife Sanctuary, known for elephant sightings and boat safaris'
      },
      {
        name: 'Marayoor',
        distance: '40 km',
        description: 'Known for its natural sandalwood forests and prehistoric dolmens'
      },
      {
        name: 'Kochi',
        distance: '130 km',
        description: 'A historic port city with colonial influences and cultural diversity'
      }
    ],
    gallery: [
      '/images/gallery/destinations/munnar1.jpg',
      '/images/gallery/destinations/munnar2.jpg',
      '/images/gallery/destinations/munnar3.jpg'
    ]
  },
  
  'fort-kochi': {
    name: 'Fort Kochi',
    tagline: 'Discover the colonial heritage and vibrant culture of this historic port city',
    heroImage: '/images/FortKochi.jpg',
    overview: `Fort Kochi, a charming coastal area in Kochi city, is a melting pot of cultures and history. 
              With its Dutch, Portuguese, and British colonial influences evident in its architecture, culture, and cuisine, 
              Fort Kochi offers visitors a unique glimpse into Kerala's multicultural past.`,
    highlights: [
      'Iconic Chinese fishing nets that line the shore',
      'Historic buildings and colonial architecture',
      'Vibrant art scene with galleries and the Kochi-Muziris Biennale',
      'Fresh seafood and multicultural cuisine',
      'Ancient religious sites including St. Francis Church and Paradesi Synagogue'
    ],
    description: `
      <h3>A Rich Historical Tapestry</h3>
      <p>Fort Kochi has a fascinating history that dates back to 1503 when the Portuguese established a trading center here. Later, the Dutch and then the British took control, each leaving their mark on this historic port town. Today, Fort Kochi is a living museum of colonial history, with European architecture, ancient religious sites, and a distinctive multicultural atmosphere.</p>
      
      <h3>Chinese Fishing Nets</h3>
      <p>One of the most iconic sights in Fort Kochi is the Chinese fishing nets that line the shore. These massive cantilevered fishing nets are believed to have been introduced by Chinese traders in the 14th century. The silhouette of these nets against the sunset creates a mesmerizing view that has become a symbol of Kerala's coastal heritage.</p>
      
      <h3>Colonial Heritage</h3>
      <p>As you stroll through the streets of Fort Kochi, you'll encounter historic buildings such as St. Francis Church, the oldest European church in India, where Vasco da Gama was originally buried. The Dutch Palace (Mattancherry Palace), built by the Portuguese and later renovated by the Dutch, houses royal artifacts and murals depicting scenes from the Ramayana and Mahabharata. The Paradesi Synagogue, built in 1568, is the oldest active synagogue in the Commonwealth nations.</p>
      
      <h3>Art and Culture</h3>
      <p>Fort Kochi has emerged as a vibrant center for arts, especially during the Kochi-Muziris Biennale, an international exhibition of contemporary art held every two years. The area is dotted with art galleries, cultural centers, and cafes that showcase the creative spirit of this historic town. The Kerala Folklore Museum offers insights into the traditional arts, crafts, and architecture of Kerala.</p>
      
      <h3>Cuisine</h3>
      <p>The culinary scene in Fort Kochi reflects its multicultural heritage. Here, you can savor Syrian Christian, Muslim, Hindu, and colonial-influenced dishes. Fresh seafood, coconut-based curries, and the famous Kerala biryani are must-try delicacies. The area is also known for its spice shops and street food stalls that offer a taste of authentic local flavors.</p>
    `,
    bestTimeToVisit: 'October to March offers pleasant weather. December is ideal for the Kochi-Muziris Biennale.',
    howToReach: {
      air: 'Cochin International Airport is about 35 km from Fort Kochi',
      train: 'Ernakulam Junction (South) and Ernakulam Town (North) are the nearest railway stations',
      road: 'Well-connected by road to major cities. Ferries from Ernakulam to Fort Kochi are also available'
    },
    activities: [
      {
        name: 'Heritage Walk',
        description: 'Explore colonial buildings, churches, and historic landmarks on a guided or self-guided walk'
      },
      {
        name: 'Art Gallery Hopping',
        description: 'Visit numerous galleries and art spaces, especially during the Kochi-Muziris Biennale'
      },
      {
        name: 'Cooking Classes',
        description: 'Learn to prepare traditional Kerala dishes with local chefs'
      },
      {
        name: 'Sunset Cruise',
        description: 'Take a boat ride in the harbor at sunset for views of the Chinese fishing nets'
      },
      {
        name: 'Kathakali Performance',
        description: 'Witness this classical dance form at the Kerala Kathakali Centre'
      }
    ],
    accommodation: [
      {
        type: 'Heritage Hotels',
        description: 'Colonial-era buildings converted into boutique accommodations'
      },
      {
        type: 'Beachfront Resorts',
        description: 'Modern properties with sea views and luxury amenities'
      },
      {
        type: 'Homestays',
        description: 'Experience local hospitality in traditional Kerala homes'
      },
      {
        type: 'Boutique Hotels',
        description: 'Stylish, intimate properties with personalized service'
      }
    ],
    nearbyDestinations: [
      {
        name: 'Alleppey',
        distance: '53 km',
        description: 'Famous for backwaters and houseboat cruises'
      },
      {
        name: 'Kumarakom',
        distance: '60 km',
        description: 'Bird sanctuary and luxury backwater resorts'
      },
      {
        name: 'Marari Beach',
        distance: '40 km',
        description: 'Pristine beach with fishing village atmosphere'
      }
    ],
    gallery: [
      '/images/gallery/destinations/fortkochi1.jpg',
      '/images/gallery/destinations/fortkochi2.jpg',
      '/images/gallery/destinations/fortkochi3.jpg'
    ]
  },
  
  'wayanad': {
    name: 'Wayanad',
    tagline: 'A lush green paradise in the Western Ghats with diverse wildlife and adventure',
    heroImage: '/images/Wayanad.jpg',
    overview: `Wayanad, nestled in the Western Ghats of Kerala, is a verdant paradise characterized by misty mountains, 
              dense forests, spice plantations, and pristine waterfalls. This highland region offers a perfect blend of 
              nature, wildlife, and adventure, making it a favorite retreat for nature lovers and outdoor enthusiasts.`,
    highlights: [
      'Breathtaking trekking trails leading to peaks and prehistoric caves',
      'Lush tea, coffee, and spice plantations covering the hillsides',
      'Abundant wildlife at Wayanad Wildlife Sanctuary',
      'Crystal-clear lakes and cascading waterfalls',
      'Ancient tribal heritage and cultural experiences'
    ],
    description: `
      <h3>Natural Splendor</h3>
      <p>Wayanad's landscape is characterized by mist-clad mountains, dense forests, and rolling hills covered with plantations. The region is part of the Nilgiri Biosphere Reserve, one of the most diverse biogeographic regions in Asia. The altitude ranges from 700 to 2100 meters above sea level, creating diverse microclimates and ecosystems that support rich biodiversity.</p>
      
      <h3>Wildlife</h3>
      <p>The Wayanad Wildlife Sanctuary, part of the Nilgiri Biosphere Reserve, is home to diverse flora and fauna, including elephants, tigers, panthers, deer, monkeys, and over 250 species of birds. The sanctuary offers safari experiences that allow visitors to witness wildlife in their natural habitat. Early morning and evening safaris provide the best chances to spot elusive animals.</p>
      
      <h3>Plantations</h3>
      <p>Wayanad is renowned for its tea, coffee, and spice plantations. The emerald green tea gardens and aromatic coffee estates dot the hillsides, offering a picturesque backdrop for your journey. Many plantations offer guided tours where visitors can learn about cultivation, processing, and sample freshly brewed beverages. The region also produces pepper, cardamom, vanilla, and other spices that contribute to Kerala's reputation as the "Spice Garden of India."</p>
      
      <h3>Adventure</h3>
      <p>For adventure enthusiasts, Wayanad offers numerous trekking opportunities. Chembra Peak, the highest in the region at 2,100 meters, rewards trekkers with panoramic views and a heart-shaped lake at its summit. The ancient Edakkal Caves, featuring prehistoric petroglyphs dating back to 6,000 BCE, offer both historical interest and a challenging climb. Other popular outdoor activities include bamboo rafting, river crossing, and zip-lining.</p>
      
      <h3>Tribal Heritage</h3>
      <p>Wayanad is home to various indigenous tribal communities, including the Paniyas, Kurumas, Adiyars, and Kurichyas, who have preserved their unique cultural heritage and traditional way of life. Visitors can learn about their crafts, rituals, and agricultural practices by visiting tribal settlements and museums dedicated to preserving their cultural legacy.</p>
    `,
    bestTimeToVisit: 'October to May offers pleasant weather ideal for outdoor activities. Avoid monsoon season (June to September) when heavy rainfall may limit exploration.',
    howToReach: {
      air: 'Nearest airports are Calicut International Airport (65 km) and Kannur International Airport (115 km)',
      train: 'Kozhikode (75 km) and Mysore (120 km) are the nearest railway stations',
      road: 'Well-connected by road from Kozhikode, Mysore, and Bangalore. The journey involves scenic drives through forest areas and ghat roads'
    },
    activities: [
      {
        name: 'Wildlife Safari',
        description: 'Explore Wayanad Wildlife Sanctuary to spot elephants, deer, and various bird species'
      },
      {
        name: 'Trekking',
        description: 'Hike to Chembra Peak, Pakshipathalam, and other trails for panoramic views'
      },
      {
        name: 'Plantation Tours',
        description: 'Visit tea, coffee, and spice plantations to learn about cultivation and processing'
      },
      {
        name: 'Cave Exploration',
        description: 'Discover ancient petroglyphs at Edakkal Caves dating back to the Neolithic era'
      },
      {
        name: 'Bamboo Rafting',
        description: 'Navigate the gentle currents of Vythiri River on traditional bamboo rafts'
      },
      {
        name: 'Waterfall Visits',
        description: 'Enjoy the scenic beauty of Soochipara, Meenmutty, and Kanthanpara waterfalls'
      }
    ],
    accommodation: [
      {
        type: 'Tree Houses',
        description: 'Eco-friendly accommodations built among treetops offering unique forest experiences'
      },
      {
        type: 'Plantation Stays',
        description: 'Cottages and bungalows set within coffee and spice estates'
      },
      {
        type: 'Mountain Resorts',
        description: 'Luxury and mid-range properties with panoramic views of the Western Ghats'
      },
      {
        type: 'Homestays',
        description: 'Family-run accommodations offering authentic local experiences and home-cooked meals'
      }
    ],
    nearbyDestinations: [
      {
        name: 'Kozhikode (Calicut)',
        distance: '75 km',
        description: 'Historic coastal city known for beaches and culinary delights'
      },
      {
        name: 'Mysore',
        distance: '120 km',
        description: 'Heritage city famous for its royal palace and cultural attractions'
      },
      {
        name: 'Silent Valley National Park',
        distance: '80 km',
        description: 'Pristine rainforest ecosystem with rare flora and fauna'
      }
    ],
    gallery: [
      '/images/gallery/destinations/wayanad1.jpg',
      '/images/gallery/destinations/wayanad2.jpg',
      '/images/gallery/destinations/wayanad3.jpg'
    ]
  },
  
  'kumarakom': {
    name: 'Kumarakom',
    tagline: 'A tranquil backwater destination with a renowned bird sanctuary',
    heroImage: '/images/Kumarakom.jpg',
    overview: `Kumarakom, a cluster of small islands on the Vembanad Lake, is a paradise of mangrove forests,
              paddy fields, and coconut groves. Famous for its bird sanctuary, houseboats, and Ayurvedic treatments,
              Kumarakom offers a perfect retreat into nature's lap with luxury experiences.`,
    highlights: [
      'Renowned bird sanctuary with local and migratory birds',
      'Luxury houseboat stays on the Vembanad Lake',
      'Authentic Ayurvedic wellness treatments',
      'Traditional Kerala architecture and village life',
      'Fresh seafood and farm-to-table dining experiences'
    ],
    description: `
      <h3>Backwater Paradise</h3>
      <p>Kumarakom is situated on the banks of the Vembanad Lake, the largest lake in Kerala and part of India's longest lake chain. The intricate network of canals, lagoons, and waterways creates a stunning backdrop for relaxation and exploration. The calm waters, swaying palms, and picturesque villages along the shores make this destination a photographer's delight.</p>
      
      <h3>Bird Sanctuary</h3>
      <p>The Kumarakom Bird Sanctuary, spread across 14 acres, is a bird watcher's paradise. Located on the eastern banks of Vembanad Lake, this sanctuary hosts numerous native birds like waterfowl, cuckoo, owl, egret, heron, and water duck, as well as migratory birds like the Siberian Stork. The best time for bird watching is between June and August, or during the migratory season from November to February. Early morning boat rides or canoe trips provide excellent opportunities to spot these winged visitors.</p>
      
      <h3>Houseboat Experience</h3>
      <p>One of the most luxurious experiences in Kumarakom is staying on a traditional houseboat, known locally as 'Kettuvallam'. These floating palaces, converted from rice barges, feature comfortable bedrooms, modern bathrooms, a dining area, and private decks. As you cruise along the backwaters, you can observe rural life along the shores, fishermen at work, and the incredible scenery of Kerala's backwater villages. The houseboats usually serve delicious traditional Kerala meals prepared fresh on board.</p>
      
      <h3>Village Life and Culture</h3>
      <p>Kumarakom offers a glimpse into authentic Kerala village life. Visitors can observe traditional occupations such as coir-making, fishing, and agriculture. The region is also known for its unique architecture, with traditional houses featuring wooden gables, sloping roofs, and courtyards designed according to Vastushastra principles. Cultural performances like Kathakali, Mohiniyattam, and Kalaripayattu are often organized by resorts for guests.</p>
      
      <h3>Ayurvedic Wellness</h3>
      <p>Kumarakom is an ideal place to experience authentic Ayurvedic treatments and therapies. Many resorts offer comprehensive wellness programs under the guidance of experienced practitioners. The serene environment, fresh air, and natural beauty complement the healing processes, making it a perfect destination for rejuvenation and holistic wellbeing.</p>
    `,
    bestTimeToVisit: 'September to March offers pleasant weather. November to February is ideal for bird watching when migratory birds visit the sanctuary.',
    howToReach: {
      air: 'Nearest airport is Cochin International Airport (85 km away)',
      train: 'Kottayam Railway Station is the closest, about 16 km from Kumarakom',
      road: 'Well-connected by road from Kochi (50 km) and Thiruvananthapuram (150 km). Water taxis and ferries are also available from nearby towns'
    },
    activities: [
      {
        name: 'Houseboat Cruises',
        description: 'Explore the backwaters on luxury houseboats for day trips or overnight stays'
      },
      {
        name: 'Bird Watching',
        description: 'Visit the Kumarakom Bird Sanctuary to spot numerous native and migratory birds'
      },
      {
        name: 'Ayurvedic Treatments',
        description: 'Experience authentic wellness therapies at specialized centers and resorts'
      },
      {
        name: 'Fishing',
        description: 'Try traditional fishing techniques with local fishermen using Chinese nets'
      },
      {
        name: 'Village Tours',
        description: 'Explore rural life, traditional crafts, and agricultural practices in nearby villages'
      },
      {
        name: 'Sunset Cruise',
        description: 'Take a small boat or canoe ride during sunset for breathtaking views over Vembanad Lake'
      }
    ],
    accommodation: [
      {
        type: 'Luxury Resorts',
        description: 'World-class properties with private villas, lakefront views, and premium amenities'
      },
      {
        type: 'Houseboats',
        description: 'Traditional kettuvallams converted into floating accommodations with varying luxury levels'
      },
      {
        type: 'Heritage Homes',
        description: 'Restored traditional Kerala houses offering authentic architectural experiences'
      },
      {
        type: 'Boutique Hotels',
        description: 'Intimate properties with personalized service and local character'
      }
    ],
    nearbyDestinations: [
      {
        name: 'Alleppey',
        distance: '35 km',
        description: 'Famous for its extensive network of backwaters and annual snake boat races'
      },
      {
        name: 'Kottayam',
        distance: '16 km',
        description: 'Known as the land of letters, latex, and lakes with significant cultural heritage'
      },
      {
        name: 'Kochi',
        distance: '50 km',
        description: 'Historic port city with colonial influences and cultural diversity'
      }
    ],
    gallery: [
      '/images/gallery/destinations/kumarakom1.jpg',
      '/images/gallery/destinations/kumarakom2.jpg',
      '/images/gallery/destinations/kumarakom3.jpg'
    ]
  },
  
  'varkala': {
    name: 'Varkala',
    tagline: 'A coastal town with dramatic cliffs, pristine beaches, and spiritual significance',
    heroImage: '/images/Varkala.jpg',
    overview: `Varkala, a coastal town in Kerala, is renowned for its stunning cliff-side setting, pristine beaches, 
              and mineral springs. This unique destination combines natural beauty with spiritual significance, as 
              it houses the 2,000-year-old Janardana Swami Temple and is considered a place for ritual purification.`,
    highlights: [
      'Dramatic red laterite cliffs overlooking the Arabian Sea',
      'Pristine Varkala Beach with opportunities for swimming and water sports',
      'Ancient Janardana Swami Temple and other spiritual sites',
      'Natural mineral springs with therapeutic properties',
      'Vibrant cliff-top promenade with shops, cafes, and restaurants'
    ],
    description: `
      <h3>The Dramatic Coastline</h3>
      <p>What sets Varkala apart from other beach destinations in Kerala is its unique cliff-side landscape. The stunning formation of sedimentary cliffs adjacent to the Arabian Sea is a rare geological feature on the otherwise flat Kerala coast. These laterite cliffs, rich in minerals, rise between 15 to 25 meters above sea level and stretch for several kilometers, offering breathtaking panoramic views of the coastline. The contrast between the red cliffs, lush green coconut palms, and the blue sea creates a picturesque setting that draws photographers and nature lovers alike.</p>
      
      <h3>Beaches</h3>
      <p>Varkala Beach, also known as Papanasam Beach, is the main beach of the town and is considered sacred. The word 'Papanasam' means 'destroyer of sins', reflecting the belief that a dip in these waters can purify the soul and wash away sins. The beach is relatively clean and safe for swimming, though the sea can get rough during monsoons. Beyond the main beach, there are several smaller, more secluded beaches like Black Beach and Odayam Beach that offer a quieter experience.</p>
      
      <h3>Spiritual Significance</h3>
      <p>Varkala holds great religious importance for Hindus. The 2,000-year-old Janardana Swami Temple, dedicated to Lord Vishnu, attracts numerous pilgrims. According to legend, the sage Narada was asked by his disciples where they could find salvation, and he threw his 'valkalam' (cloth made of tree bark) which landed in Varkala, hence the name. The town is also known for performing last rites and ceremonies for departed souls, with many traveling here to immerse the ashes of their loved ones in the sacred waters.</p>
      
      <h3>Mineral Springs</h3>
      <p>Near the Papanasam Beach, there is a natural mineral spring that is believed to have medicinal properties. These waters are said to cure various ailments, especially skin conditions. The spring originates from the cliff and flows to meet the Arabian Sea. Many visitors take a dip in these mineral-rich waters for their therapeutic benefits.</p>
      
      <h3>Cliff-Top Culture</h3>
      <p>The cliff top of Varkala has developed into a vibrant promenade lined with shops, restaurants, cafes, and yoga centers. This area, often referred to as 'Varkala Cliff', offers a unique blend of traditional Kerala architecture and a laid-back, cosmopolitan atmosphere. Here, visitors can shop for souvenirs, enjoy fresh seafood with ocean views, participate in yoga sessions, or simply relax at a cafe watching the sunset. The cliff is particularly lively in the evenings when both tourists and locals come out to enjoy the sea breeze and the vibrant atmosphere.</p>
    `,
    bestTimeToVisit: 'November to March offers pleasant weather and clear skies, perfect for beach activities and cliff walks. Avoid the monsoon season (June to August) when the sea can be rough.',
    howToReach: {
      air: 'Nearest airport is Trivandrum International Airport (40 km away)',
      train: 'Varkala has its own railway station (Varkala Sivagiri) well-connected to major cities',
      road: 'Well-connected by road from Thiruvananthapuram (40 km) and other major cities in Kerala'
    },
    activities: [
      {
        name: 'Beach Activities',
        description: 'Swimming, sunbathing, and water sports at Varkala Beach'
      },
      {
        name: 'Cliff Walking',
        description: 'Stroll along the cliff pathway for panoramic views of the Arabian Sea'
      },
      {
        name: 'Temple Visits',
        description: 'Explore the ancient Janardana Swami Temple and other spiritual sites'
      },
      {
        name: 'Yoga and Meditation',
        description: 'Join sessions at various centers along the cliff'
      },
      {
        name: 'Ayurvedic Treatments',
        description: 'Experience traditional Ayurvedic therapies at specialized centers'
      },
      {
        name: 'Shopping',
        description: 'Browse beachside shops for handicrafts, clothing, jewelry, and souvenirs'
      }
    ],
    accommodation: [
      {
        type: 'Cliff-Top Resorts',
        description: 'Properties offering stunning views of the Arabian Sea'
      },
      {
        type: 'Beach Resorts',
        description: 'Accommodations with direct access to the beach'
      },
      {
        type: 'Ayurvedic Retreats',
        description: 'Centers offering accommodation combined with wellness programs'
      },
      {
        type: 'Guesthouses',
        description: 'Budget-friendly options popular with backpackers and long-term travelers'
      }
    ],
    nearbyDestinations: [
      {
        name: 'Thiruvananthapuram',
        distance: '40 km',
        description: 'The capital city with historic temples, museums, and palaces'
      },
      {
        name: 'Kollam',
        distance: '35 km',
        description: 'An old port city known for its backwaters and cashew processing'
      },
      {
        name: 'Kappil Beach',
        distance: '5 km',
        description: 'A serene spot where the backwaters meet the sea'
      }
    ],
    gallery: [
      '/images/gallery/destinations/varkala1.jpg',
      '/images/gallery/destinations/varkala2.jpg',
      '/images/gallery/destinations/varkala3.jpg'
    ]
  },
  
  'thekkady': {
    name: 'Thekkady',
    tagline: 'A wildlife haven nestled in the spice-scented hills of Kerala',
    heroImage: '/images/Thekkady.jpg',
    overview: `Thekkady, home to the famous Periyar Wildlife Sanctuary, is a nature lover's paradise nestled in the 
              Cardamom Hills of Kerala. This beautiful hill station offers the unique opportunity to observe wildlife 
              in their natural habitat while surrounded by the aromatic spice plantations that have made the region famous.`,
    highlights: [
      'Periyar Tiger Reserve and Wildlife Sanctuary with diverse flora and fauna',
      'Boat safaris on Periyar Lake for wildlife spotting',
      'Spice plantations producing cardamom, pepper, cinnamon, and other aromatics',
      'Tribal villages with rich indigenous culture and traditions',
      'Adventure activities including bamboo rafting, jungle patrol, and trekking'
    ],
    description: `
      <h3>Periyar Wildlife Sanctuary</h3>
      <p>The crown jewel of Thekkady is the Periyar Tiger Reserve, one of India's most well-maintained wildlife sanctuaries. Spanning an area of 925 sq km, this protected forest is home to tigers, elephants, sambar deer, gaur (Indian bison), lion-tailed macaques, and over 265 species of birds. What makes this sanctuary unique is the 26 sq km artificial lake formed by the Mullaperiyar Dam, allowing visitors to observe wildlife coming to the water's edge during boat safaris. The lush evergreen and deciduous forests surrounding the lake create a stunning backdrop for wildlife viewing.</p>
      
      <h3>Spice Plantations</h3>
      <p>Thekkady and the surrounding Cardamom Hills region are synonymous with spice cultivation. The area's cool climate, elevation, and rainfall pattern create ideal conditions for growing cardamom, pepper, cinnamon, clove, nutmeg, vanilla, and other aromatic spices. Numerous plantations offer guided tours where visitors can learn about spice cultivation, processing techniques, and the historical spice trade that once attracted explorers from distant lands to Kerala's shores. These tours often include spice tasting sessions and opportunities to purchase fresh, high-quality spices directly from the source.</p>
      
      <h3>Tribal Heritage</h3>
      <p>The forests around Thekkady are home to several indigenous tribal communities, including the Mannans, Paliyans, Mala Arayas, Uralis, and Malayarayans. These communities have preserved their unique cultural traditions, knowledge of medicinal plants, and sustainable forest practices for generations. Visitors can learn about their lifestyle, crafts, and traditions through cultural performances and visits to tribal villages, providing insights into the harmonious relationship these communities have maintained with the forest ecosystem.</p>
      
      <h3>Adventure Activities</h3>
      <p>Beyond wildlife viewing, Thekkady offers a range of adventure activities for outdoor enthusiasts. The Forest Department organizes bamboo rafting expeditions on Periyar Lake, border hiking, jungle patrol with tribal trackers, and night jungle walks. These eco-friendly activities provide immersive experiences in the forest while contributing to conservation efforts. For those seeking less strenuous activities, nature walks along established trails offer opportunities to observe smaller wildlife, birds, butterflies, and the rich flora of the region.</p>
      
      <h3>Ayurvedic Experiences</h3>
      <p>Like many destinations in Kerala, Thekkady is known for authentic Ayurvedic treatments and therapies. The clean air, natural surroundings, and availability of fresh medicinal herbs from the forest create an ideal environment for wellness experiences. Many resorts and dedicated Ayurveda centers offer traditional therapies administered by trained practitioners, providing rejuvenation for both body and mind.</p>
    `,
    bestTimeToVisit: 'September to May is ideal, with October to February offering the best wildlife sightings. Avoid heavy monsoon months (June-August) when some activities may be limited.',
    howToReach: {
      air: 'Nearest airports are Madurai Airport (140 km) and Cochin International Airport (190 km)',
      train: 'Closest railway stations are Kottayam (114 km) and Madurai (140 km)',
      road: 'Well-connected by road from major cities in Kerala and Tamil Nadu. The drive from Kochi takes about 4-5 hours through scenic mountain roads'
    },
    activities: [
      {
        name: 'Boat Safaris',
        description: 'Take a boat ride on Periyar Lake to spot wildlife coming to the water\'s edge'
      },
      {
        name: 'Spice Plantation Tours',
        description: 'Visit aromatic cardamom, pepper, and other spice gardens with guided tours'
      },
      {
        name: 'Nature Walks',
        description: 'Join guided walking tours in designated areas of the sanctuary to spot birds and small animals'
      },
      {
        name: 'Bamboo Rafting',
        description: 'Experience a silent bamboo rafting expedition through the core areas of the forest'
      },
      {
        name: 'Elephant Interactions',
        description: 'Visit ethical elephant camps for feeding and bathing sessions'
      },
      {
        name: 'Kathakali and Kalaripayattu Shows',
        description: 'Watch performances of Kerala\'s traditional dance-drama and martial art forms'
      }
    ],
    accommodation: [
      {
        type: 'Jungle Lodges',
        description: 'Eco-friendly accommodations near or within the sanctuary boundaries'
      },
      {
        type: 'Plantation Stays',
        description: 'Cottages and bungalows within spice plantations offering unique aromatic experiences'
      },
      {
        type: 'Luxury Resorts',
        description: 'High-end properties with premium amenities and forest or mountain views'
      },
      {
        type: 'Homestays',
        description: 'Family-run accommodations offering authentic local experiences and home-cooked Kerala cuisine'
      }
    ],
    nearbyDestinations: [
      {
        name: 'Munnar',
        distance: '100 km',
        description: 'Famous hill station known for its tea plantations and cool climate'
      },
      {
        name: 'Madurai',
        distance: '140 km',
        description: 'Ancient Tamil city renowned for the magnificent Meenakshi Amman Temple'
      },
      {
        name: 'Kumily',
        distance: '4 km',
        description: 'Spice trading town with local markets and cultural attractions'
      }
    ],
    gallery: [
      '/images/Thekkady1.jpg',
      '/images/Thekkady2.jpg',
      '/images/Thekkady3.jpg'
    ]
  },
  
  'kovalam': {
    name: 'Kovalam Beach',
    tagline: 'A crescent-shaped paradise of golden sands and azure waters',
    heroImage: 'https://images.unsplash.com/photo-1584551246679-0daf3d275d0f',
    overview: `Kovalam, once a quiet fishing village, has transformed into one of Kerala's most famous beach destinations. 
              Characterized by its three crescent-shaped beaches separated by rocky outcroppings, Kovalam offers 
              pristine shorelines, ayurvedic rejuvenation, water sports, and a relaxed coastal atmosphere.`,
    highlights: [
      'Three distinct crescent-shaped beaches with golden sands',
      'The iconic Vizhinjam Lighthouse offering panoramic coastal views',
      'Authentic Ayurvedic wellness centers and treatments',
      'Fresh seafood cuisine and beachside dining experiences',
      'Water sports including surfing, parasailing, and catamaran rides'
    ],
    description: `
      <h3>The Three Beaches</h3>
      <p>Kovalam is famous for its three adjacent crescent-shaped beaches that together form the coastline of this popular destination. Lighthouse Beach, the largest and southernmost of the three, gets its name from the prominent 30-meter tall Vizhinjam Lighthouse that stands on Kurumkal Hill. The red and white striped lighthouse is not only a navigational aid but also a major tourist attraction, offering spectacular panoramic views of the coastline from its observation deck. Hawa Beach (also called Eve's Beach) is the middle beach, known for its laid-back atmosphere and array of budget accommodations. Samudra Beach, the northernmost and least crowded, maintains a more authentic fishing village ambiance where visitors can observe local fishermen bringing in their daily catch.</p>
      
      <h3>Beach Activities</h3>
      <p>The calm waters and relatively flat seabed make Kovalam's beaches ideal for swimming during most of the year, except during monsoon season. Water sports enthusiasts can enjoy surfing, especially at Lighthouse Beach where beginners can find surfing schools offering lessons. Other popular activities include parasailing, water skiing, catamaran sailing, and banana boat rides. For those preferring relaxation, sunbathing on the golden sands or taking leisurely walks along the shoreline while watching spectacular sunsets are perfect ways to unwind.</p>
      
      <h3>Ayurvedic Heritage</h3>
      <p>Kovalam is renowned as one of Kerala's premier destinations for Ayurvedic treatments and therapies. Numerous wellness centers and resorts offer traditional Ayurvedic programs under the guidance of experienced practitioners. The beachside location provides a soothing environment conducive to healing and rejuvenation. Common offerings include therapeutic massages, detoxification programs, stress management techniques, and treatments for specific health conditions. Many visitors come to Kovalam specifically for extended Ayurvedic wellness retreats, combining treatments with yoga and meditation.</p>
      
      <h3>Cuisine and Dining</h3>
      <p>The beachfront promenade at Kovalam is lined with restaurants serving fresh seafood and a variety of cuisines. Local Kerala specialties feature prominently, with fish curry, karimeen pollichathu (pearl spot fish grilled in banana leaf), prawn masala, and seafood thalis being popular choices. Many restaurants set up tables on the beach in the evening, allowing guests to dine under the stars with the sound of waves as background music. Besides local cuisine, visitors can find continental, Chinese, and other international options to satisfy diverse palates.</p>
      
      <h3>Cultural Experiences</h3>
      <p>Beyond beach activities, Kovalam offers opportunities to experience local culture and traditions. The nearby fishing villages maintain their traditional way of life, and early morning visits allow tourists to witness fishermen setting out to sea or returning with their catch. Cultural performances of Kathakali, Mohiniyattam, and other classical art forms are regularly organized at major resorts. The proximity to Thiruvananthapuram, Kerala's capital, also provides easy access to historical sites, museums, and temples for those interested in cultural exploration.</p>
    `,
    bestTimeToVisit: 'October to March offers ideal beach weather with clear skies and moderate temperatures. Avoid monsoon season (June to September) when swimming may be restricted due to rough seas.',
    howToReach: {
      air: 'Nearest airport is Trivandrum International Airport, just 16 km away',
      train: 'Thiruvananthapuram Central Railway Station is about 15 km from Kovalam',
      road: 'Well-connected by road from Thiruvananthapuram and other cities in Kerala. Auto-rickshaws and taxis are readily available'
    },
    activities: [
      {
        name: 'Beach Activities',
        description: 'Swimming, sunbathing, and beach volleyball on the golden shores'
      },
      {
        name: 'Water Sports',
        description: 'Surfing, parasailing, water skiing, and catamaran rides for adventure enthusiasts'
      },
      {
        name: 'Lighthouse Visit',
        description: 'Climb the Vizhinjam Lighthouse for panoramic views of the coastline'
      },
      {
        name: 'Ayurvedic Treatments',
        description: 'Experience traditional wellness therapies at specialized centers'
      },
      {
        name: 'Sunset Watching',
        description: 'Enjoy spectacular sunset views from the beach or lighthouse'
      },
      {
        name: 'Shopping',
        description: 'Browse beachside shops for handicrafts, clothing, jewelry, and souvenirs'
      }
    ],
    accommodation: [
      {
        type: 'Beach Resorts',
        description: 'Luxury and mid-range properties with direct beach access and sea views'
      },
      {
        type: 'Ayurvedic Retreats',
        description: 'Specialized centers offering accommodation combined with wellness programs'
      },
      {
        type: 'Boutique Hotels',
        description: 'Stylish, intimate properties with personalized service'
      },
      {
        type: 'Budget Guesthouses',
        description: 'Affordable options popular with backpackers and long-term travelers'
      }
    ],
    nearbyDestinations: [
      {
        name: 'Thiruvananthapuram',
        distance: '15 km',
        description: 'Kerala\'s capital city with historical sites, museums, and the famous Padmanabhaswamy Temple'
      },
      {
        name: 'Poovar',
        distance: '30 km',
        description: 'A serene spot where backwaters, river, sea, and beach meet, known for floating cottages'
      },
      {
        name: 'Kanyakumari',
        distance: '85 km',
        description: 'India\'s southernmost tip where three oceans meet, famous for its sunset and sunrise views'
      }
    ],
    gallery: [
      'https://images.unsplash.com/photo-1584551246679-0daf3d275d0f',
      'https://images.unsplash.com/photo-1580289723869-74d64b027819',
      'https://images.unsplash.com/photo-1626621331169-5f34be280ed9'
    ]
  },
  // More destinations can be added here following the same structure
};

const DestinationDetailPage = () => {
  const { id } = useParams();
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // In a real app, you would fetch data from an API here
    // For now, we'll use the static data
    if (destinationsData[id]) {
      setDestination(destinationsData[id]);
      setLoading(false);
    } else {
      // Handle case where destination is not found
      setLoading(false);
    }
  }, [id]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!destination) {
    return (
      <div className="container not-found">
        <h2>Destination Not Found</h2>
        <p>Sorry, the destination you're looking for doesn't exist.</p>
        <Link to="/destinations" className="btn btn-primary">Back to Destinations</Link>
      </div>
    );
  }

  return (
    <div className="destination-detail-page">
      <div className="destination-hero" style={{ backgroundImage: `url(${destination.heroImage})` }}>
        <div className="destination-hero-overlay"></div>
        <div className="destination-hero-content">
          <h1>{destination.name}</h1>
          <p>{destination.tagline}</p>
        </div>
      </div>

      <div className="destination-navigation">
        <div className="container">
          <ul className="destination-nav-links">
            <li><a href="#overview">Overview</a></li>
            <li><a href="#highlights">Highlights</a></li>
            <li><a href="#details">Details</a></li>
            <li><a href="#activities">Activities</a></li>
            <li><a href="#accommodation">Stay</a></li>
            <li><a href="#gallery">Gallery</a></li>
          </ul>
        </div>
      </div>

      <section id="overview" className="destination-overview">
        <div className="container">
          <div className="overview-content">
            <div className="overview-text">
              <h2>Discover {destination.name}</h2>
              <p>{destination.overview}</p>
              
              <div className="destination-info-cards">
                <div className="info-card">
                  <FaCalendarAlt className="info-icon" />
                  <h4>Best Time To Visit</h4>
                  <p>{destination.bestTimeToVisit}</p>
                </div>
                <div className="info-card">
                  <FaRupeeSign className="info-icon" />
                  <h4>Budget Estimate</h4>
                  <p>₹3,000 - ₹15,000 per day depending on accommodation and activities</p>
                </div>
                <div className="info-card">
                  <FaClock className="info-icon" />
                  <h4>Ideal Duration</h4>
                  <p>2-3 days for a comprehensive experience</p>
                </div>
              </div>
            </div>
            <div className="overview-map">
              <div className="how-to-reach">
                <h4>How to Reach</h4>
                <div className="reach-options">
                  <div className="reach-option">
                    <h5>By Air</h5>
                    <p>{destination.howToReach.air}</p>
                  </div>
                  <div className="reach-option">
                    <h5>By Train</h5>
                    <p>{destination.howToReach.train}</p>
                  </div>
                  <div className="reach-option">
                    <h5>By Road</h5>
                    <p>{destination.howToReach.road}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="highlights" className="destination-highlights">
        <div className="container">
          <div className="section-header">
            <h2>Highlights of {destination.name}</h2>
            <p>Top experiences you shouldn't miss</p>
          </div>
          <div className="highlights-list">
            {destination.highlights.map((highlight, index) => (
              <div className="highlight-item" key={index}>
                <span className="highlight-number">{index + 1}</span>
                <p>{highlight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="details" className="destination-details">
        <div className="container">
          <div className="section-header">
            <h2>About {destination.name}</h2>
            <p>Detailed information about this beautiful destination</p>
          </div>
          <div className="details-content">
            <div dangerouslySetInnerHTML={{ __html: destination.description }} />
          </div>
        </div>
      </section>

      <section id="activities" className="destination-activities">
        <div className="container">
          <div className="section-header">
            <h2>Things to Do in {destination.name}</h2>
            <p>Explore the best activities and experiences</p>
          </div>
          <div className="activities-grid">
            {destination.activities.map((activity, index) => (
              <div className="activity-card" key={index}>
                <FaSuitcase className="activity-icon" />
                <h3>{activity.name}</h3>
                <p>{activity.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="accommodation" className="destination-accommodation">
        <div className="container">
          <div className="section-header">
            <h2>Where to Stay</h2>
            <p>Accommodation options in and around {destination.name}</p>
          </div>
          <div className="accommodation-options">
            {destination.accommodation.map((option, index) => (
              <div className="accommodation-card" key={index}>
                <h3>{option.type}</h3>
                <p>{option.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="nearby-destinations">
        <div className="container">
          <div className="section-header">
            <h2>Nearby Destinations</h2>
            <p>Extend your Kerala adventure with these nearby places</p>
          </div>
          <div className="nearby-grid">
            {destination.nearbyDestinations.map((nearby, index) => (
              <div className="nearby-card" key={index}>
                <h3>{nearby.name}</h3>
                <p className="nearby-distance">{nearby.distance} from {destination.name}</p>
                <p>{nearby.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="gallery" className="destination-gallery">
        <div className="container">
          <div className="section-header">
            <h2>{destination.name} Gallery</h2>
            <p>Visual journey through this beautiful destination</p>
          </div>
          <div className="gallery-grid">
            {destination.gallery.map((image, index) => (
              <div className="gallery-item" key={index}>
                <img src={image} alt={`${destination.name} - ${index + 1}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="destination-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Experience {destination.name}?</h2>
            <p>Let us help you plan the perfect itinerary for your Kerala adventure</p>
            <Link to="/contact" className="btn btn-primary">Plan Your Trip</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DestinationDetailPage; 